import { useCallback, useState } from "react";
import type { ExplorerItem } from "../types";
import { useKeyboardNavigation } from "./use-keyboard-navigation";
import { isTaskItem } from "../utils/item-lock";

export type SelectMode = "single" | "toggle";

type Props = {
  enabled: boolean;
  windowId?: string;
  selectableIds: readonly number[];
  disabledIds: ReadonlySet<number>;
  /** Plain click ("single") or Cmd/Ctrl+click ("toggle"). */
  onSelectItem: (itemId: number, mode: SelectMode) => void;
  /** Shift+click range and Cmd/Ctrl+A. */
  onSelectSet: (itemIds: Set<number>) => void;
  /** Arrow-key navigation; falls back to onSelectSet. */
  onNavigate?: (itemId: number, itemIds: Set<number>) => void;
};

export const useTreeSelection = ({
  enabled,
  windowId,
  selectableIds,
  disabledIds,
  onSelectItem,
  onSelectSet,
  onNavigate,
}: Props) => {
  const [anchorId, setAnchorId] = useState<number | null>(null);

  const handleNavigate = useCallback(
    (itemId: number, itemIds: Set<number>) => {
      setAnchorId(itemId);
      if (onNavigate) onNavigate(itemId, itemIds);
      else onSelectSet(itemIds);
    },
    [onNavigate, onSelectSet]
  );

  const { containerRef, setFocusedItem, getVisibleItemIds, cancelPendingNavigation } =
    useKeyboardNavigation({
      enabled,
      windowId,
      onNavigate: handleNavigate,
    });

  const handleItemClick = useCallback(
    (item: ExplorerItem, e: React.MouseEvent) => {
      if (!isTaskItem(item) || disabledIds.has(item.id)) return;
      cancelPendingNavigation();
      setFocusedItem(item.id);

      if (e.shiftKey && anchorId !== null) {
        const visibleIds = getVisibleItemIds();
        const from = visibleIds.indexOf(anchorId);
        const to = visibleIds.indexOf(item.id);
        if (from === -1 || to === -1) return;

        const [start, end] = from < to ? [from, to] : [to, from];
        onSelectSet(new Set(visibleIds.slice(start, end + 1).filter((id) => !disabledIds.has(id))));
        return;
      }

      setAnchorId(item.id);
      onSelectItem(item.id, e.ctrlKey || e.metaKey ? "toggle" : "single");
    },
    [
      anchorId,
      disabledIds,
      getVisibleItemIds,
      onSelectItem,
      onSelectSet,
      setFocusedItem,
      cancelPendingNavigation,
    ]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLElement>) => {
      const isSelectAll = (e.metaKey || e.ctrlKey) && !e.altKey && e.key.toLowerCase() === "a";
      if (!isSelectAll) return;

      e.preventDefault(); // stop the browser's "select all text"
      e.stopPropagation();
      if (selectableIds.length === 0) return;

      cancelPendingNavigation();
      setAnchorId(null);
      onSelectSet(new Set(selectableIds));
    },
    [selectableIds, onSelectSet, cancelPendingNavigation]
  );

  return { containerRef, handleItemClick, handleKeyDown, setFocusedItem, getVisibleItemIds };
};
